import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_DARK } from "@/lib/brand";

export default function Login() {
  const { user, setUser, loading } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name };
      const { data } = await api.post(path, payload);
      setUser(data);
      toast.success(`Welcome${data.name ? ", " + data.name : ""}!`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Brand panel */}
      <div className="relative hidden lg:flex lg:w-1/2 bg-ink text-white p-14 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 blueprint-grid-dark" />
        <Link to="/" className="relative">
          <img src={LOGO_DARK} alt="On The Brink — Visionary Growth Partners" className="h-14 w-auto object-contain" />
        </Link>
        <div className="relative">
          <h2 className="font-heading font-extrabold text-4xl tracking-tight leading-tight">Client Portal.</h2>
          <p className="mt-4 text-slate-300 max-w-md leading-relaxed">
            Access your growth dashboard, campaign performance, and HaloFlow agent activity in one secure place.
          </p>
        </div>
        <p className="relative text-sm text-slate-500">On The Brink™ – An AI-First Service Agency.</p>
      </div>

      {/* Form panel */}
      <div className="flex flex-1 items-center justify-center p-6 sm:p-12 bg-white">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                    className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slatey hover:text-ink mb-8"><ArrowLeft className="h-4 w-4" /> Back to site</Link>
          <div className="flex h-12 w-12 items-center justify-center bg-ink text-white"><Lock className="h-5 w-5" /></div>
          <h1 className="mt-6 font-heading font-bold text-ink text-3xl">{mode === "login" ? "Sign in" : "Create account"}</h1>
          <p className="mt-2 text-slatey text-sm">{mode === "login" ? "Welcome back. Enter your credentials to continue." : "Set up your client portal access."}</p>

          <button onClick={googleLogin} data-testid="google-login-button"
                  className="mt-8 w-full inline-flex items-center justify-center gap-3 border border-slate-200 px-4 py-3.5 text-sm font-semibold text-ink hover:bg-slate-50 transition-colors">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-4">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs uppercase tracking-wider text-slate-400">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            {mode === "register" && (
              <div>
                <label className="block text-xs uppercase tracking-wider font-semibold text-slatey mb-2">Full Name</label>
                <input required value={form.name} onChange={update("name")} data-testid="register-name" className="otb-input" placeholder="Jane Doe" />
              </div>
            )}
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-slatey mb-2">Email</label>
              <input required type="email" value={form.email} onChange={update("email")} data-testid="login-email" className="otb-input" placeholder="you@company.com" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-slatey mb-2">Password</label>
              <input required type="password" value={form.password} onChange={update("password")} data-testid="login-password" className="otb-input" placeholder="••••••••" />
            </div>
            <button type="submit" disabled={busy} data-testid="login-submit"
                    className="w-full bg-ember text-white px-6 py-3.5 text-sm font-semibold hover:bg-ember-dark transition-colors disabled:opacity-60">
              {busy ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slatey">
            {mode === "login" ? "Need an account?" : "Already have an account?"}{" "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="toggle-auth-mode"
                    className="font-semibold text-ember hover:underline">
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
      <style>{`.otb-input{width:100%;border:1px solid #E2E8F0;background:#fff;padding:0.75rem 0.875rem;font-size:0.9rem;color:#0F172A;outline:none;transition:border-color .15s}.otb-input:focus{border-color:#EA580C}`}</style>
    </div>
  );
}
